import React, { useEffect, useState } from "react";
import placeholder from "./placeholder.jpg";
import {
  FaCamera,
  FaCheck,
  FaTimes,
  FaSearchPlus,
  FaSearchMinus,
  FaEye,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import Cropper from "react-easy-crop";
import getCroppedImg from "./cropimg";

const ProfilePhoto = ({ userId }) => {
  const [photoURL, setPhotoURL] = useState(placeholder);
  const [uploading, setUploading] = useState(false);
  const [successMsgVisible, setSuccessMsgVisible] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [imageForCropping, setImageForCropping] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showOptions, setShowOptions] = useState(false);

  // ✅ Fetch profile photo for the given userId
  useEffect(() => {
    if (!userId) return;

    const fetchPhoto = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/get-student-photo/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const data = await res.json();

        if (res.ok && data.success) {
          setPhotoURL(data.photo);
        } else {
          setPhotoURL(placeholder);
        }
      } catch (err) {
        console.error("Error fetching photo:", err);
        setPhotoURL(placeholder);
      }
    };

    fetchPhoto();
  }, [userId]);

  const onCropComplete = (_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      const imageUrl = URL.createObjectURL(file);
      setImageForCropping(imageUrl);
      setShowCropper(true);
      setShowOptions(false);
    }
  };

  const handleCropConfirm = async () => {
    if (!imageForCropping || !croppedAreaPixels) return;

    setUploading(true);
    setErrorMsg("");
    setSuccessMsgVisible(false);

    try {
      const croppedImageFile = await getCroppedImg(
        imageForCropping,
        croppedAreaPixels
      );

      const formData = new FormData();
      formData.append("user_id", userId);
      formData.append("photo", croppedImageFile);

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/upload-student-photo`,
        {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const data = await res.json();

      if (res.ok && data.success) {
        // ✅ Refresh the image from backend
        const refreshed = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/get-user-photo/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const newData = await refreshed.json();

        if (newData.success) {
          setPhotoURL(newData.photo);
        }

        setSuccessMsgVisible(true);
        setTimeout(() => setSuccessMsgVisible(false), 2500);
      } else {
        setErrorMsg(data.message || "Upload failed");
      }
    } catch (err) {
      console.error("Upload failed:", err);
      setErrorMsg("Upload error");
    } finally {
      setShowCropper(false);
      setUploading(false);
      setImageForCropping(null);
    }
  };

  return (
    <div className="flex flex-col items-center gap-4 relative z-10">
      {/* Cropper Modal */}
      <AnimatePresence>
        {showCropper && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex flex-col items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="relative w-full max-w-md h-96 bg-gray-900 rounded-lg overflow-hidden shadow-xl"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
            >
              <Cropper
                image={imageForCropping}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                cropShape="round"
                showGrid={false}
              />

              <div className="absolute bottom-4 left-0 right-0 flex flex-col items-center px-4">
                <div className="flex items-center gap-4 w-full max-w-xs">
                  <FaSearchMinus className="text-white" />
                  <input
                    type="range"
                    min="1"
                    max="3"
                    step="0.1"
                    value={zoom}
                    onChange={(e) => setZoom(e.target.value)}
                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                  <FaSearchPlus className="text-white" />
                </div>
              </div>
            </motion.div>

            <div className="flex gap-4 mt-6">
              <button
                onClick={() => {
                  setShowCropper(false);
                  setImageForCropping(null);
                }}
                className="px-6 py-3 bg-gray-600 hover:bg-gray-500 text-white rounded-full flex items-center gap-2 transition-colors"
              >
                <FaTimes /> Cancel
              </button>
              <button
                onClick={handleCropConfirm}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-full flex items-center gap-2 transition-colors"
                disabled={uploading}
              >
                <FaCheck /> {uploading ? "Processing..." : "Confirm"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* View Photo Modal */}
      <AnimatePresence>
        {showViewModal && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowViewModal(false)}
          >
            <motion.div
              className="relative max-w-2xl max-h-2xl"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={photoURL || placeholder}
                alt="Profile Preview"
                className="w-full h-full max-w-md max-h-md object-contain rounded-lg shadow-2xl"
              />
              <button
                onClick={() => setShowViewModal(false)}
                className="absolute -top-3 -right-3 bg-red-500 hover:bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors shadow-lg"
              >
                <FaTimes className="text-sm" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Profile Photo Container */}
      <div
        className="relative w-14 h-14 rounded-full overflow-hidden border-2 border-blue-600 shadow-md bg-gray-100"
        onMouseEnter={() => setShowOptions(true)}
        onMouseLeave={() => setShowOptions(false)}
      >
        {/* Profile Image */}
        <img
          src={photoURL || placeholder}
          alt="Profile"
          className="w-full h-full object-cover transition-transform duration-300"
          onError={(e) => (e.target.src = placeholder)}
        />

        {/* Uploading Overlay */}
        {uploading && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <motion.div
              className="w-7 h-7 border-4 border-green-500 border-t-transparent rounded-full animate-spin"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            />
          </div>
        )}

        {/* Hover Overlay */}
        <AnimatePresence>
          {showOptions && (
            <motion.div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center gap-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {/* VIEW BUTTON */}
              <motion.button
                onClick={() => setShowViewModal(true)}
                className="w-10 h-10 bg-blue-500 hover:bg-blue-400 text-white rounded-full flex items-center justify-center transition-all duration-200 shadow-lg"
                whileHover={{ scale: 1.12 }}
                whileTap={{ scale: 0.95 }}
                title="View Photo"
              >
                <FaEye className="text-lg" />
              </motion.button>

              {/* EDIT BUTTON */}
              <label
                htmlFor={`fileInput-${userId}`}
                className="w-10 h-10 bg-green-500 hover:bg-green-400 text-white rounded-full flex items-center justify-center transition-all duration-200 cursor-pointer shadow-lg"
                title="Update Photo"
              >
                <FaCamera className="text-lg" />
              </label>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Hidden File Input */}
        <input
          id={`fileInput-${userId}`}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      {/* Success and Error Messages */}
      <AnimatePresence>
        {successMsgVisible && (
          <motion.p
            className="text-green-600 font-medium text-sm text-center"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            Profile photo updated successfully!
          </motion.p>
        )}
      </AnimatePresence>

      {errorMsg && (
        <motion.p
          className="text-red-500 text-sm text-center max-w-xs"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {errorMsg}
        </motion.p>
      )}
    </div>
  );
};

export default ProfilePhoto;
